import { createContext, type JSX, useContext } from 'solid-js'
import { MainModule } from '../../../features/main-module'
import { type GetSongsInteractor } from '../../../features/engagement/domain/interactors/get-songs.interactor'
import { type GetVideosInteractor } from '../../../features/engagement/domain/interactors/get-videos.interactor'
import { type GetEventsInteractor } from '../../../features/engagement/domain/interactors/get-events.interactor'
import { type GetCommunityInteractor } from '../../../features/engagement/domain/interactors/get-community.interactor'
import { type GetChatInteractor } from '../../../features/engagement/domain/interactors/get-chat.interactor'
import { type GetUsersMediaInteractor } from '../../../features/engagement/domain/interactors/get-users-media.interactor'
import { type GetUsersEventsInteractor } from '../../../features/engagement/domain/interactors/get-users-events.interactor'
import { type GetUsersForumInteractor } from '../../../features/engagement/domain/interactors/get-users-forum.interactor'
import { type GetUsersChatInteractor } from '../../../features/engagement/domain/interactors/get-users-chat.interactor'
import { type GetUsersTotalsInteractor } from '../../../features/engagement/domain/interactors/get-users-totals.interactor'
import { type GetSectionsSummaryInteractor } from '../../../features/engagement/domain/interactors/get-sections-summary.interactor'
import { type GetSectionsComparisonInteractor } from '../../../features/engagement/domain/interactors/get-sections-comparison.interactor'

interface EngagementContextType {
  getSongs: GetSongsInteractor
  getVideos: GetVideosInteractor
  getEvents: GetEventsInteractor
  getCommunity: GetCommunityInteractor
  getChat: GetChatInteractor
  getUsersMedia: GetUsersMediaInteractor
  getUsersEvents: GetUsersEventsInteractor
  getUsersForum: GetUsersForumInteractor
  getUsersChat: GetUsersChatInteractor
  getUsersTotals: GetUsersTotalsInteractor
  getSectionsSummary: GetSectionsSummaryInteractor
  getSectionsComparison: GetSectionsComparisonInteractor
}

const CorisEngagementContext = createContext<EngagementContextType>()

export const CorisEngagementProvider = (props: { children: JSX.Element }): JSX.Element => {
  const engagementComponent = MainModule.getEngagementComponent()

  const defaultContextValue: EngagementContextType = {
    getSongs: engagementComponent.provideGetSongs(),
    getVideos: engagementComponent.provideGetVideos(),
    getEvents: engagementComponent.provideGetEvents(),
    getCommunity: engagementComponent.provideGetCommunity(),
    getChat: engagementComponent.provideGetChat(),
    getUsersMedia: engagementComponent.provideGetUsersMedia(),
    getUsersEvents: engagementComponent.provideGetUsersEvents(),
    getUsersForum: engagementComponent.provideGetUsersForum(),
    getUsersChat: engagementComponent.provideGetUsersChat(),
    getUsersTotals: engagementComponent.provideGetUsersTotals(),
    getSectionsSummary: engagementComponent.provideGetSectionsSummary(),
    getSectionsComparison: engagementComponent.provideGetSectionsComparison()
  }

  return <CorisEngagementContext.Provider value={defaultContextValue}>{props.children}</CorisEngagementContext.Provider>
}

export const useCorisEngagement = (): EngagementContextType => {
  const context = useContext(CorisEngagementContext)
  if (!context) {
    throw new Error('useCorisEngagement must be used inside a CorisEngagementProvider')
  }
  return context
}
